import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { CurriculumItemsAPI } from "@/lib/curriculum-items-api";
import { SyllabusAPI } from "@/lib/syllabus-api";
import { TYPE_LABELS, colorOf } from "@/lib/exam-tile-tokens";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Upload, Loader2, Trash2, FileText, Image as ImageIcon,
  Calendar, BookOpen, ChevronsUpDown, Sparkles, Wand2,
} from "lucide-react";
import { toast } from "sonner";

const UPLOAD_ROLES = [
  "super_admin", "inst_admin", "principal", "campus_admin",
  "hod", "coordinator", "it_admin", "teacher",
];

// Sensible defaults — schools can already match these via the dropdown.
const GRADE_OPTIONS = [
  "Pre-KG", "LKG", "UKG",
  ...Array.from({ length: 12 }, (_, i) => `Grade ${i + 1}`),
];

const SUBJECT_OPTIONS = [
  "English", "Hindi", "Mathematics", "Science",
  "Biology", "Physics", "Chemistry",
  "Social Studies", "History", "Geography", "Civics", "Economics",
  "Computer Science", "EVS",
  "Sanskrit", "French", "German", "Spanish", "Kannada", "Tamil", "Telugu", "Marathi", "Bengali", "Malayalam",
  "Physical Education", "Art & Craft", "Music", "Dance", "Moral Science",
];

const fmtSize = (n) => {
  if (!n) return "—";
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n > 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const isImage = (ct) => (ct || "").startsWith("image/");

export default function CurriculumAssessment() {
  const { structureId, nodeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canUpload = (user?.roles || []).some((r) => UPLOAD_ROLES.includes(r));
  const qc = useQueryClient();

  const seqQ = useQuery({
    queryKey: ["curriculum-seq", structureId],
    queryFn: () => CurriculumItemsAPI.sequence(structureId),
  });
  const itemsQ = useQuery({
    queryKey: ["curriculum-items", structureId, nodeId],
    queryFn: () => CurriculumItemsAPI.items({ structure_id: structureId, node_id: nodeId }),
  });

  const node = useMemo(
    () => (seqQ.data?.nodes || []).find((n) => n.id === nodeId),
    [seqQ.data, nodeId]
  );

  // Auto-complete options pulled from past uploads on this structure (for chapter / topic / subject / grade)
  const allItems = itemsQ.data?.items || [];
  const distinct = (key) => Array.from(new Set(allItems.map((i) => i[key]).filter(Boolean)));

  if (seqQ.isLoading) {
    return (
      <AppShell>
        <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      </AppShell>
    );
  }

  if (seqQ.isError || !seqQ.data) {
    return (
      <AppShell>
        <div className="p-8" data-testid="curr-assessment-not-found">
          <Button variant="outline" onClick={() => navigate("/curriculum")}><ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back</Button>
          <h1 className="text-2xl font-display font-bold mt-4">Plan not found</h1>
        </div>
      </AppShell>
    );
  }

  if (!node) {
    return (
      <AppShell>
        <div className="p-8" data-testid="curr-assessment-not-found">
          <Button variant="outline" onClick={() => navigate("/curriculum")}><ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back</Button>
          <h1 className="text-2xl font-display font-bold mt-4">Assessment not found in this plan.</h1>
        </div>
      </AppShell>
    );
  }

  const c = colorOf(node);

  return (
    <AppShell>
      <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="curr-assessment-page">
        <Button
          variant="outline"
          size="default"
          onClick={() => navigate("/curriculum")}
          data-testid="curr-back-btn"
          className="font-semibold shadow-sm"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Curriculum
        </Button>

        {/* Header card showing the assessment tile + plan context */}
        <div className={`${c.bg} ${c.border} ${c.text} border-2 rounded-xl p-5 shadow-sm`} data-testid="curr-assessment-header">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mb-1">
                {TYPE_LABELS[node.type] || node.type} · {seqQ.data.name}
              </div>
              <h1 className="font-display font-extrabold tracking-tight text-3xl">{node.name}</h1>
              <div className="text-xs mt-1.5 opacity-80 flex items-center gap-2 flex-wrap">
                {node.max_marks != null && <span>{node.max_marks} marks</span>}
                {node.weight_pct != null && <span>· {node.weight_pct}% weightage</span>}
                {node.scheduled_month && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{node.scheduled_month}</span>}
                {seqQ.data.board && <span>· {seqQ.data.board.toUpperCase()}</span>}
                {seqQ.data.academic_year && <span>· {seqQ.data.academic_year}</span>}
              </div>
            </div>
            <Badge className={`${c.pill} text-white border-0`} data-testid="curr-assessment-item-count">
              <FileText className="h-3 w-3 mr-1" /> {allItems.length} curriculum item{allItems.length === 1 ? "" : "s"}
            </Badge>
          </div>
        </div>

        {canUpload && (
          <UploadCard
            structureId={structureId}
            nodeId={nodeId}
            existingGrades={distinct("grade")}
            existingSubjects={distinct("subject")}
            existingChapters={distinct("chapter")}
            existingTopics={distinct("topics")}
            onDone={() => qc.invalidateQueries({ queryKey: ["curriculum-items", structureId, nodeId] })}
          />
        )}

        <ItemsList items={allItems} isLoading={itemsQ.isLoading} canUpload={canUpload} onRemoved={() => qc.invalidateQueries({ queryKey: ["curriculum-items", structureId, nodeId] })} />
      </div>
    </AppShell>
  );
}

function UploadCard({ structureId, nodeId, existingGrades, existingSubjects, existingChapters, existingTopics, onDone }) {
  const fileRef = useRef(null);
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [chapter, setChapter] = useState("");
  const [topics, setTopics] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autofilling, setAutofilling] = useState(false);

  // AI-extracted chapter/topic suggestions for this grade+subject
  const sugQ = useQuery({
    queryKey: ["syllabus-suggestions", grade, subject],
    queryFn: () => SyllabusAPI.suggestions({ grade, subject }),
    enabled: !!grade && !!subject,
  });

  // When the chapter changes, suggest its topics from the scanned book
  const scannedChapters = sugQ.data?.chapters || [];
  const topicsByChapter = sugQ.data?.topics_by_chapter || {};
  const scannedTopicsForChapter = (chapter && topicsByChapter[chapter]) || [];
  const matchedSyllabus = sugQ.data?.syllabus;

  // Reset chapter when grade/subject changes (so the suggestion list lines up)
  useEffect(() => { setChapter(""); }, [grade, subject]);

  const mergedGrades = useMemo(
    () => Array.from(new Set([...existingGrades, ...GRADE_OPTIONS])),
    [existingGrades]
  );
  const mergedSubjects = useMemo(
    () => Array.from(new Set([...existingSubjects, ...SUBJECT_OPTIONS])).sort(),
    [existingSubjects]
  );
  // Chapter options = scanned chapters (top) + existing user-typed chapters from past uploads
  const chapterOptions = useMemo(
    () => Array.from(new Set([...scannedChapters, ...existingChapters])),
    [scannedChapters, existingChapters]
  );
  // Topic suggestions = scanned topics for the currently-picked chapter + existing topic strings
  const topicOptions = useMemo(
    () => Array.from(new Set([...scannedTopicsForChapter, ...existingTopics])),
    [scannedTopicsForChapter, existingTopics]
  );

  const canSubmit = !!file && !!grade && !!subject && !!chapter.trim() && !busy;

  const autofillFromSyllabus = async () => {
    if (!matchedSyllabus?.file_url || !matchedSyllabus.filename) return;
    setAutofilling(true);
    try {
      // Fetch the syllabus PDF via the API client so the auth token is attached
      const apiRes = await SyllabusAPI.suggestions({ grade, subject }); // refresh to be sure
      const meta = apiRes?.syllabus;
      if (!meta?.file_id) throw new Error("No matching syllabus file");
      // Download with auth header via fetch + Bearer token from localStorage
      const token = localStorage.getItem("access_token");
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${base}/api/v1/syllabus/files/${meta.file_id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`Download failed (${r.status})`);
      const blob = await r.blob();
      const dropped = new File([blob], meta.filename, { type: meta.content_type || "application/pdf" });
      setFile(dropped);
      // Tell the file input what we have (browsers don't allow setting .files directly;
      // we display the chosen file via the {file && ...} hint below)
      if (fileRef.current) {
        try {
          const dt = new DataTransfer();
          dt.items.add(dropped);
          fileRef.current.files = dt.files;
        } catch { /* not all browsers allow this, ok */ }
      }
      toast.success(`Auto-filled with ${meta.filename}`);
    } catch (e) {
      toast.error(e?.message || "Auto-fill failed");
    } finally {
      setAutofilling(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    const fd = new FormData();
    fd.append("structure_id", structureId);
    fd.append("node_id", nodeId);
    if (grade) fd.append("grade", grade);
    if (subject) fd.append("subject", subject);
    if (chapter.trim()) fd.append("chapter", chapter.trim());
    if (topics.trim()) fd.append("topics", topics.trim());
    fd.append("file", file);
    setBusy(true);
    try {
      await CurriculumItemsAPI.upload(fd);
      toast.success("Curriculum item uploaded");
      // Reset chapter/topics/file but keep grade+subject for fast batch uploads
      setChapter(""); setTopics(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-card border border-border rounded-xl shadow-sm p-5 space-y-4"
      data-testid="curr-upload-card"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Upload className="h-4 w-4 text-primary" />
          <h2 className="font-display font-bold text-base">Attach curriculum material</h2>
        </div>
        {grade && subject && scannedChapters.length > 0 && (
          <Badge className="bg-violet-500/15 text-violet-700 hover:bg-violet-500/20 border-0" data-testid="curr-scan-hint">
            <Sparkles className="h-3 w-3 mr-1" />
            {scannedChapters.length} chapters auto-suggested from scanned syllabus
          </Badge>
        )}
      </div>

      {/* Auto-fill banner */}
      {matchedSyllabus && (
        <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-violet-500/5 border border-violet-300/40" data-testid="curr-autofill-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Wand2 className="h-4 w-4 text-violet-600 shrink-0" />
            <div className="min-w-0 text-[12px]">
              <div className="font-semibold text-violet-800 truncate">Matching syllabus found · {matchedSyllabus.filename}</div>
              <div className="text-violet-700/70 truncate">Auto-fill the file picker and use the same PDF as the attached material.</div>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={autofilling}
            onClick={autofillFromSyllabus}
            className="border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid="curr-autofill-btn"
          >
            {autofilling ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Wand2 className="h-3 w-3 mr-1" />}
            Auto-fill
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">
            1. Grade <span className="text-rose-600">*</span>
          </Label>
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger data-testid="curr-grade-select"><SelectValue placeholder="Select grade…" /></SelectTrigger>
            <SelectContent>
              {mergedGrades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">
            2. Subject <span className="text-rose-600">*</span>
          </Label>
          <Select value={subject} onValueChange={setSubject}>
            <SelectTrigger data-testid="curr-subject-select"><SelectValue placeholder="Select subject…" /></SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {mergedSubjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">
            3. Chapter <span className="text-rose-600">*</span>
            {sugQ.isFetching && grade && subject && <Loader2 className="h-2.5 w-2.5 animate-spin inline ml-1 opacity-50" />}
          </Label>
          <ComboInput
            value={chapter}
            onChange={setChapter}
            placeholder="e.g. Plants and Animals"
            options={chapterOptions}
            data-testid="curr-chapter-input"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">
            4. Topics <span className="text-muted-foreground text-[10px] font-normal">(comma-separated)</span>
          </Label>
          <ComboInput
            value={topics}
            onChange={setTopics}
            placeholder="Photosynthesis, Decomposition…"
            options={topicOptions}
            data-testid="curr-topics-input"
          />
          {scannedTopicsForChapter.length > 0 && (
            <button
              type="button"
              onClick={() => setTopics(scannedTopicsForChapter.join(", "))}
              className="text-[10px] text-violet-700 mt-1 hover:underline inline-flex items-center gap-1"
              data-testid="curr-use-scanned-topics"
            >
              <Sparkles className="h-2.5 w-2.5" /> Use all {scannedTopicsForChapter.length} scanned topics
            </button>
          )}
        </div>
      </div>

      <div className="flex items-end gap-3 flex-wrap pt-3 border-t border-border">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block">
            File <span className="text-rose-600">*</span>
            <span className="text-muted-foreground text-[10px] font-normal ml-1">(PDF / DOC / DOCX / image · max 25MB)</span>
          </Label>
          <Input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf,.png,.jpg,.jpeg,.webp,.heic,.heif,.gif"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="curr-file-input"
            className="h-10"
          />
          {file && <div className="text-[11px] text-muted-foreground mt-1">{file.name} · {fmtSize(file.size)}</div>}
        </div>
        <Button type="submit" disabled={!canSubmit} className="h-10" data-testid="curr-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
          Save to this assessment
        </Button>
      </div>
    </form>
  );
}

function ComboInput({ value, onChange, placeholder, options, ...rest }) {
  // Free-text input + plain absolute-positioned suggestion dropdown
  // sourced from past uploads. We avoid Radix Popover here because
  // anchoring it to the input + a separate chevron trigger creates
  // state races; a plain absolute dropdown is simpler and reliable.
  const [open, setOpen] = useState(false);
  const filtered = (options || [])
    .filter((o) => o.toLowerCase().includes((value || "").toLowerCase()))
    .slice(0, 8);
  const showDropdown = open && filtered.length > 0;
  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="pr-8"
        {...rest}
      />
      {options.length > 0 && (
        <button
          type="button"
          tabIndex={-1}
          aria-label="Show suggestions"
          onMouseDown={(e) => { e.preventDefault(); setOpen((v) => !v); }}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center text-muted-foreground hover:text-foreground"
        >
          <ChevronsUpDown className="h-3.5 w-3.5" />
        </button>
      )}
      {showDropdown && (
        <div
          className="absolute z-30 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-[220px] overflow-y-auto"
          data-testid={`${rest["data-testid"] || "combo"}-popover`}
        >
          {filtered.map((o) => (
            <button
              type="button"
              key={o}
              onMouseDown={(e) => { e.preventDefault(); onChange(o); setOpen(false); }}
              className="block w-full text-left px-2.5 py-1.5 hover:bg-muted/60 text-sm"
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemsList({ items, isLoading, canUpload, onRemoved }) {
  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.filename}"? This cannot be undone.`)) return;
    try {
      await CurriculumItemsAPI.remove(item.id);
      toast.success("Curriculum item deleted");
      onRemoved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl shadow-sm p-5" data-testid="curr-items-list">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="h-4 w-4 text-primary" />
        <h2 className="font-display font-bold text-base">Curriculum material ({items.length})</h2>
      </div>
      {isLoading ? (
        <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          <FileText className="h-10 w-10 mx-auto opacity-30 mb-2" />
          No material attached yet. Use the form above to upload the first one.
        </div>
      ) : (
        <div className="divide-y divide-border border border-border rounded-md overflow-hidden">
          {items.map((row) => (
            <div key={row.id} className="px-4 py-3 hover:bg-muted/40 flex items-start gap-3" data-testid={`curr-item-${row.id}`}>
              <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${isImage(row.content_type) ? "bg-emerald-500/10 text-emerald-700" : "bg-primary/10 text-primary"}`}>
                {isImage(row.content_type) ? <ImageIcon className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <a
                  href={row.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-sm hover:underline truncate inline-block max-w-full"
                  data-testid={`curr-item-link-${row.id}`}
                >
                  {row.filename}
                </a>
                <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>{fmtSize(row.size)}</span>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtDate(row.uploaded_at)}</span>
                </div>
                <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                  {row.grade && <Badge variant="outline" className="text-[10px]">{row.grade}</Badge>}
                  {row.subject && <Badge variant="outline" className="text-[10px]">{row.subject}</Badge>}
                  {row.chapter && <Badge variant="outline" className="text-[10px]">{row.chapter}</Badge>}
                  {row.topics && row.topics.split(",").map((t, i) => (
                    <Badge key={i} className="text-[10px] bg-sky-500/10 text-sky-700 hover:bg-sky-500/15 border-0">{t.trim()}</Badge>
                  ))}
                </div>
              </div>
              {canUpload && (
                <Button size="icon" variant="ghost" onClick={() => remove(row)} title="Delete" data-testid={`curr-item-delete-${row.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
